import { ReactNode } from 'react'
import { Link, usePage } from '@inertiajs/react'
import type { SharedProps } from '@/types'

type Vars = Record<string, string | number>

/**
 * Frontend i18n over the `frontend` locale subtree shared by the Localized
 * concern. All UI copy lives in config/locales/{en,ru}.yml — components read
 * it with t()/tx() so switching the Rails locale re-renders the whole site.
 */
export function useTranslation() {
  const { translations, locale } = usePage<SharedProps>().props

  /** Raw subtree lookup (objects/arrays, e.g. legal page sections). */
  const tx = <T,>(key: string): T => {
    const node = key
      .split('.')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .reduce<any>((acc, part) => (acc && typeof acc === 'object' ? acc[part] : undefined), translations)
    return node as T
  }

  /** String lookup with %{var} interpolation; echoes the key when missing. */
  const t = (key: string, vars?: Vars): string => {
    const raw = tx<unknown>(key)
    if (typeof raw !== 'string') return key
    return interpolate(raw, vars)
  }

  return { t, tx, locale }
}

/** Replace %{name} placeholders (same syntax as Rails i18n). */
export function interpolate(text: string, vars?: Vars): string {
  if (!vars) return text
  return text.replace(/%\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match
  )
}

/**
 * Render a translated string that may contain **bold** spans and
 * [label](href) links (internal hrefs become Inertia links). Keeps rich
 * formatting in the YAML instead of splitting strings across keys.
 */
export function renderRich(text: string): ReactNode[] {
  const nodes: ReactNode[] = []
  const pattern = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)/g
  let cursor = 0
  let match: RegExpExecArray | null
  let key = 0

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > cursor) nodes.push(text.slice(cursor, match.index))

    if (match[1] !== undefined) {
      nodes.push(<strong key={key++}>{match[1]}</strong>)
    } else {
      const [, , label, href] = match
      const style = { textDecoration: 'underline', textUnderlineOffset: 3 }
      nodes.push(
        href.startsWith('/') ? (
          <Link key={key++} href={href} style={style}>
            {label}
          </Link>
        ) : (
          <a key={key++} href={href} style={style}>
            {label}
          </a>
        )
      )
    }
    cursor = match.index + match[0].length
  }

  if (cursor < text.length) nodes.push(text.slice(cursor))
  return nodes
}
